import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import Slider from '../components/common/Slider';
import ProductCard from '../components/product/ProductCard';
import { SliderItem, Category, Product } from '../types';
import { productService } from '../services/productService';
import { categoryService } from '../services/categoryService';
import { sliderService } from '../services/sliderService';
import { dailyDealService } from '../services/dailyDealService';
import { FaStar, FaFire, FaGift, FaArrowRight, FaHistory, FaShoppingCart, FaPlus, FaMinus, FaTimes } from 'react-icons/fa';
import { FiChevronLeft, FiChevronRight, FiEye } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { categoryHistory } from '../utils/categoryHistory';
import { IconType } from 'react-icons';
import { getImageUrl, API_URL } from '../config';
import DailyDealCard from '../components/product/DailyDealCard';
import Preloader from '../components/common/Preloader';
import BrandsCarousel from '../components/common/BrandsCarousel';
import FloatingSpeedDial from '../components/common/FloatingSpeedDial';
import { useAuth } from '../context/AuthContext';
import { useConsultation } from '../context/ConsultationContext';

// Definiciones de tipos para iconos
const ArrowRight = FaArrowRight as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const Times = FaTimes as React.ComponentType<React.SVGProps<SVGSVGElement>>;
const Eye = FiEye as React.ComponentType<React.SVGProps<SVGSVGElement>>;

// Interfaces
interface CategoryWithProducts {
  category: {
    image_url: string;
    id: string;
    name: string;
    description?: string;
  };
  products: Product[];
}

interface DailyDeal {
  id: string;
  product_id: string;
  price: number;
  start_date: string;
  end_date: string;
  active: boolean;
  product?: Product;
}

// Nueva interfaz para espacios
interface SpaceProduct {
  id?: number;
  product_id: number;
  x: number;
  y: number;
  destacado: boolean;
  orden?: number;
  name?: string;
  image_url?: string;
}

interface Space {
  id: number;
  name?: string;
  image_url: string | null;
  created_at: string;
  updated_at: string;
  products: SpaceProduct[];
  destacados: SpaceProduct[];
}

// Styled components
const HomeContainer = styled.div`
  min-height: calc(100vh - 200px);
`;

const Section = styled.section`
  padding: 3rem 0;
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 3rem;
  color: var(--primary-blue);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  
  @media (max-width: 768px) {
    font-size: 20px;
    margin-bottom: 2rem;
  }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
    max-width: 90%;
    padding: 10px;
    margin: 0 auto;
  }
`;

const HeroSection = styled.section`
  padding: 0;
  max-width: 1366px; /* Ajustado a la medida exacta de tus banners */
  width: 95%;
  margin: 28px auto 0; /* Aumentado 3px */
  
  @media (max-width: 768px) {
    max-width: 100%;
    width: 100%;
    margin-top: 43px; /* Aumentado 3px */
  }
  
  @media (max-width: 480px) {
    max-width: 100%;
  }

  @media (max-width: 1366px) {
    max-width: 100%;
  }
    
`;

const ViewAllButton = styled.button`
  display: block;
  margin: 2rem auto 0;
  padding: 12px 24px;
  background: var(--primary-blue);
  color: white;
  border: none;
  border-radius: var(--border-radius);
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);
  
  &:hover {
    background: #081050;
  }
`;

const CategorySelector = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 2rem;
  padding: 0.5rem 0;
  justify-content: center;
  position: relative;
  @media (max-width: 768px) {
    gap: 0.4rem;
    padding: 0.5rem 0.5rem;
    margin-bottom: 1rem;
  }
`;

// Container de productos de categorías - DESKTOP GRID + MÓVIL SCROLL
const CategoryProductsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  
  /* Desktop: SIEMPRE 5 productos en una fila */
  @media (min-width: 769px) {
    padding: 0 20px;
  }
  
  /* Móvil: Container para scroll horizontal */
  @media (max-width: 768px) {
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    scroll-behavior: smooth;
    padding: 0 0 8px 0;
    width: 100vw;
    min-width: 0;
    max-width: 100vw;
    box-sizing: border-box;
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE and Edge */
    &::-webkit-scrollbar {
      display: none; /* Chrome, Safari, Opera */
    }
    &::after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      width: 30px;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8));
      pointer-events: none;
      z-index: 2;
    }
  }
`;

const CategoryProductsGrid = styled.div`
  /* Desktop: GRID de exactamente 5 productos */
  @media (min-width: 769px) {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 1.5rem;
  }
  
  /* Móvil: FLEX para scroll horizontal estilo Promart */
  @media (max-width: 768px) {
    display: flex;
    gap: 0.75rem;
    padding: 0 20px;
    width: max-content;
    min-width: 0;
    > * {
      flex: 0 0 170px;
      width: 170px;
      min-width: 170px;
      max-width: 170px;
      box-sizing: border-box;
    }
  }
`;

// Sección de productos de categorías - MEJORADO Y RESPONSIVE  
const CategoryProductsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  align-items: center;
  width: 100%;
  position: relative;
  
  @media (max-width: 768px) {
    gap: 1rem;
    padding: 0 10px;
    
    /* Indicador de scroll para productos */
    &::before {
      position: absolute;
      top: -20px;
      right: 20px;
      font-size: 0.7rem;
      color: #999;
      font-style: italic;
      z-index: 3;
    }
  }
`;

// Contenedor de la sección de categorías con indicador de scroll
const CategorySection = styled.div`
  position: relative;
  width: 60%;
  margin: 0 auto;
  
  @media (max-width: 768px) {
  width: 100%;
    /* Indicador de scroll en móvil */
    &::before {
     
      position: absolute;
      top: -25px;
      right: 20px;
      font-size: 0.7rem;
      color: #999;
      font-style: italic;
      z-index: 1;
    }
  }
`;

const DynamicCategoryButton = styled.button`
  background: linear-gradient(135deg, var(--primary-orange), #FDBF37);
  color: white;
  border: none;
  border-radius: 25px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(255, 140, 66, 0.3);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 1.5rem;
  justify-content: center;
  
  /* Desktop */
  @media (min-width: 769px) {
    padding: 1rem 2rem;
    font-size: 1.1rem;
    
    &:hover {
      background: linear-gradient(135deg, #FDBF37, var(--primary-orange));
      transform: translateY(-3px);
      box-shadow: 0 6px 20px rgba(255, 140, 66, 0.4);
    }

    &:active {
      transform: translateY(-1px);
    }
  }
  
  /* Móvil */
  @media (max-width: 768px) {
    padding: 0.875rem 1.5rem;
    font-size: 0.95rem;
    margin-top: 1rem;
    width: 100%;
    max-width: 300px;
    
    &:hover {
      background: linear-gradient(135deg, #FDBF37, var(--primary-orange));
      transform: translateY(-2px);
      box-shadow: 0 4px 15px rgba(255, 140, 66, 0.5);
    }
    
    &:active {
      transform: scale(0.98);
    }
  }
`;

const CategoryButton = styled.button<{ isActive: boolean }>`
  background: ${props => props.isActive ? 'var(--primary-orange)' : 'white'};
  color: ${props => props.isActive ? 'white' : 'var(--primary-orange)'};
  border: 2px solid var(--primary-orange);
  border-radius: 18px;
  cursor: pointer;
  font-weight: 600;
  white-space: nowrap;
  transition: all 0.3s ease;
  flex-shrink: 0;
  font-size: 0.85rem;
  text-transform: none;
  letter-spacing: 0.2px;
  padding: 0.4rem 1rem;
  min-width: fit-content;
  @media (max-width: 768px) {
    font-size: 0.75rem;
    padding: 0.3rem 0.7rem;
    border-radius: 14px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--text-color);
  font-style: italic;
`;

const VisitedCategoriesGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  justify-content: center;

  /* Cada tarjeta tendrá un ancho más compacto */
  > * {
    flex: 0 0 280px;
    max-width: 280px;
  }

  @media (max-width: 768px) {
    gap: 0.75rem;
    padding: 0 15px;
    
    > * {
      flex: 0 0 100%;
      max-width: 100%;
    }
  }
`;

const VisitedCategoryCard = styled.div`
  position: relative;
  cursor: pointer;
  border-radius: 20px;
  overflow: hidden;
  background: #f8f9fa;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.4s ease;
  height: 340px; /* Aumentado para acomodar imágenes más grandes */
  display: flex;
  flex-direction: column;
  border: 1px solid #eee;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12);
    border-color: var(--primary-blue);
  }

  @media (max-width: 768px) {
    height: 250px;
  }
`;

const CategoryImage = styled.img`
  width: 90px;
  height: 90px;
  object-fit: contain;
  transition: all 0.5s ease;
  flex-shrink: 0;
  margin-right: 5px; /* Evitar que toque el borde derecho */

  ${VisitedCategoryCard}:hover & {
    transform: scale(1.1) rotate(5deg);
  }

  @media (max-width: 768px) {
    width: 70px;
    height: 70px;
  }
`;

const CategoryOverlay = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`;

const CategoryTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  height: 110px; /* Altura fija para que todas las cabeceras sean iguales */
  margin-bottom: 0.5rem;
`;

const CategoryInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1rem 1rem 0 1rem;

  @media (max-width: 768px) {
    padding: 0.75rem 0.75rem 0 0.75rem;
  }
`;

const CategoryName = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  color: #2c3e50;
  font-weight: 800;
  line-height: 1.2;
  margin-bottom: 0.25rem;
  /* Forzar máximo 2 líneas para mantener simetría */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const CategorySubtitle = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: #7f8c8d;
  font-weight: 500;
  margin-bottom: 1.5rem;
`;

const CategoryArrow = styled.div`
  background: var(--primary-blue);
  color: white;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  font-weight: bold;
  transition: all 0.3s ease;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);

  ${VisitedCategoryCard}:hover & {
    background: #081050;
    transform: translateX(4px);
  }

  @media (max-width: 768px) {
    width: 24px;
    height: 24px;
    font-size: 0.8rem;
  }
`;

const ProductsPreview = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(8px);
  padding: 10px;
  border-top: 1px solid rgba(0,0,0,0.05);
  margin-top: auto; /* Empujar siempre al fondo */

  img {
    width: 100%;
    height: 115px; /* Aumentado de 85px a 115px */
    object-fit: contain;
    transition: all 0.3s ease;
    padding: 6px;
    border-radius: 8px;
    background: white;
    border: 1px solid #f1f1f1;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
    
    &:hover {
      transform: scale(1.05);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
  }

  @media (max-width: 768px) {
    gap: 3px;
    padding: 6px;
    
    img {
      height: 75px; /* Mantener tamaño pequeño en móvil */
      padding: 6px;
      border-radius: 6px;
    }
  }
`;

const SmallSliderSection = styled.section`
  padding: 2rem 0;
  background: white;
  overflow: hidden;
`;

const SmallSliderContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
  
  /* PREVENCIÓN ADICIONAL DE OVERFLOW */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 100px;
    background: linear-gradient(90deg, transparent, white);
    pointer-events: none;
    z-index: 5;
  }
`;

// Eliminar estas variables no utilizadas (líneas 291 y 309 aproximadamente):
// const SmallSliderTrack = styled.div`
//   display: flex;
//   gap: 1rem;
//   overflow-x: auto;
//   padding: 1rem;
//   scrollbar-width: thin;
//   &::-webkit-scrollbar {
//     height: 6px;
//   }
//   &::-webkit-scrollbar-track {
//     background: #f1f1f1;
//   }
//   &::-webkit-scrollbar-thumb {
//     background: var(--primary-blue);
//     border-radius: 3px;
//   }
// `;

// const SmallSliderItem = styled.div`
//   flex: 0 0 auto;
//   width: 220px;
//   border-radius: 8px;
//   overflow: hidden;
//   box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
//   transition: transform 0.3s ease;

//   &:hover {
//     transform: translateY(-5px);
//   }
// `;

const DailyDealsCarouselContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  overflow: hidden;
  padding: 0;
  box-sizing: border-box;
  
  /* En móvil permitir scroll horizontal natural */
  @media (max-width: 768px) {
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    scroll-behavior: smooth;
    
    /* Ocultar scrollbar pero mantener funcionalidad */
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE and Edge */
    
    &::-webkit-scrollbar {
      display: none; /* Chrome, Safari, Opera */
    }
  }
`;

const DailyDealsCarouselTrack = styled.div.withConfig({
  shouldForwardProp: (prop) => !['currentSlide', 'isMobile', 'totalProducts'].includes(prop),
})<{ currentSlide: number; isMobile: boolean; totalProducts: number }>`
  display: flex;
  transition: transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  
  /* Desktop: slides completos con transform */
  transform: translateX(-${props => {
    if (!props.isMobile) {
      // Desktop: slides completos como antes
      return `${props.currentSlide * 100}%`;
    } else {
      // Móvil: sin transform, usar scroll natural
      return '0%';
    }
  }});
  
  width: ${props => {
    if (!props.isMobile) {
      // Desktop: 100% como antes para slides completos
      return '100%';
    } else {
      // Móvil: ancho calculado para forzar scroll horizontal
      return `${props.totalProducts * 45}vw`;
    }
  }};
  
  /* En móvil: quitar transiciones para scroll natural */
  @media (max-width: 768px) {
    transition: none;
    transform: none !important;
    width: ${props => `${props.totalProducts * 45}vw`} !important;
  }
`;

// Styled components para scroll móvil SOLAMENTE
const MobileScrollIndicator = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 1.5rem;
    padding: 0 20px;
    background: rgba(255, 255, 255, 0.95);
    border-radius: 12px;
    padding: 12px 20px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
    border: 1px solid rgba(255, 107, 0, 0.2);
  }
`;

const ScrollBar = styled.div`
  width: 250px; /* Más larga y visible */
  height: 8px; /* Más gruesa */
  background: linear-gradient(90deg, #f0f0f0, #e8e8e8);
  border-radius: 4px;
  position: relative;
  overflow: hidden;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(0, 0, 0, 0.1);
`;

const ScrollThumb = styled.div<{ progress: number }>`
  position: absolute;
  height: 100%;
  background: linear-gradient(90deg, var(--primary-orange), #ff8c42, #ff6b00);
  border-radius: 4px;
  width: 50px; /* Más ancho para ser más visible */
  left: ${props => props.progress}%;
  transition: left 0.2s ease-out; /* Transición más suave */
  box-shadow: 
    0 2px 8px rgba(255, 107, 0, 0.4),
    0 0 0 1px rgba(255, 255, 255, 0.3);
  
  /* Efecto de brillo */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(90deg, 
      transparent 0%, 
      rgba(255, 255, 255, 0.3) 50%, 
      transparent 100%
    );
    border-radius: 4px;
  }
`;

const DailyDealsCarouselSlide = styled.div.withConfig({
  shouldForwardProp: (prop) => !['totalProducts', 'isMobile'].includes(prop),
})<{ totalProducts?: number; isMobile?: boolean }>`
  min-width: 100%;
  width: 100%;
  display: flex;
  gap: 0.5%;
  padding: 1.5rem 110px;
  box-sizing: border-box;
  align-items: stretch;
  justify-content: center;
  
  @media (max-width: 1024px) {
    padding: 1rem 90px;
  }
  
  @media (max-width: 768px) {
    /* En móvil: ancho calculado para forzar scroll horizontal */
    min-width: ${props => props.isMobile && props.totalProducts ? 
      `${(props.totalProducts * 40) + (props.totalProducts * 3)}vw` : 'fit-content'};
    width: ${props => props.isMobile && props.totalProducts ? 
      `${(props.totalProducts * 40) + (props.totalProducts * 3)}vw` : 'fit-content'};
    padding: 1rem 20px;
    justify-content: flex-start;
    gap: 20px;
  }
  
  @media (max-width: 480px) {
    padding: 1rem 15px;
    gap: 20px;
  }
`;

const DailyDealsItemWrapper = styled.div`
  flex: 0 0 auto;
  width: 19.8%;
  max-width: 19.8%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  
  /* Desktop: mostrar máximo 5 elementos por slide */
  &:nth-child(n+6) {
    display: none;
  }
  
  @media (max-width: 1024px) {
    width: calc(25% - 0.5%);
    max-width: calc(25% - 0.5%);
    
    &:nth-child(n+5) {
      display: none;
    }
  }
  
  @media (max-width: 768px) {
    /* Para ver exactamente 2.5 productos */
    width: 38vw; /* 38% del viewport para mostrar 2.5 productos */
    max-width: 38vw;
    min-width: 38vw;
    
    /* En móvil mostrar todos los productos para scroll libre */
    &:nth-child(n+3),
    &:nth-child(n+5),
    &:nth-child(n+6) {
      display: flex;
    }
  }

  /* Contenido de la card */
  > div {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    overflow: hidden;
    border-radius: 15px;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
    background: white;
    transition: all 0.3s ease;
    border: 2px solid transparent;
    
    &:hover {
      transform: translateY(-8px);
      box-shadow: 0 12px 35px rgba(0, 0, 0, 0.18);
      border-color: var(--primary-orange);
    }
    
    img {
      height: 140px;
      object-fit: cover;
      width: 100%;
      border-radius: 13px 13px 0 0;
    }
    
    > div:last-child {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      padding: 0.75rem;
    }
    
    @media (max-width: 768px) {
      border-radius: 12px;
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
      min-height: 320px;
      
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 15px rgba(0, 0, 0, 0.15);
      }
      
      img {
        height: 140px;
        object-fit: cover;
      }
      
      > div:last-child {
        padding: 0.75rem;
        font-size: 0.85rem;
        
        h3 {
          font-size: 0.9rem;
          line-height: 1.2;
          margin-bottom: 0.5rem;
          font-weight: 600;
        }
        
        p {
          font-size: 0.8rem;
          line-height: 1.3;
          margin-bottom: 0.5rem;
          color: #666;
        }
      }
    }
  }
`;

const DailyDealsNavButton = styled.button<{ direction: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => props.direction === 'left' ? 'left: 25px;' : 'right: 25px;'}
  background: #FDBF37;
  color: white;
  border: none;
  border-radius: 50%;
  width: 55px;
  height: 55px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 200;
  box-shadow: 0 6px 20px rgba(255, 107, 0, 0.3);
  font-size: 1.2rem;
  border: 3px solid white;
  
  &:hover:not(:disabled) {
    background: #FDBF37;
    transform: translateY(-50%) scale(1.15);
    box-shadow: 0 8px 30px rgba(253, 191, 55, 0.4);
  }
  
  &:active:not(:disabled) {
    transform: translateY(-50%) scale(1.05);
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    background: rgba(0, 0, 0, 0.3);
    border-color: rgba(255, 255, 255, 0.5);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
  
  /* OCULTAR COMPLETAMENTE en móvil */
  @media (max-width: 768px) {
    display: none !important;
  }
`;

const IconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

// Icon Components
const IconComponent = ({ icon: Icon, color }: { icon: IconType; color: string }) => {
  const IconElement = Icon as React.ElementType;
  return (
    <IconWrapper>
      <IconElement style={{ color }} />
    </IconWrapper>
  );
};

const StyledHistoryIcon = () => (
  <IconComponent icon={FaHistory} color="var(--primary-color)" />
);



const StyledGiftIcon = () => (
  <IconComponent icon={FaGift} color="var(--primary-color)" />
);

const StyledFireIcon = () => (
  <IconComponent icon={FaFire} color="var(--primary-color)" />
);

const StyledArrowRightIcon = () => (
  <IconComponent icon={FaArrowRight} color="inherit" />
);

// Agregar estos nuevos componentes de iconos
const StyledChevronLeftIcon = () => (
  <IconComponent icon={FiChevronLeft} color="inherit" />
);

const StyledChevronRightIcon = () => (
  <IconComponent icon={FiChevronRight} color="inherit" />
);

// Remover completamente los styled components MobileIndicators e Indicator

// 1. Eliminar el indicador visual de scroll para productos
// (Simplemente no renderizar <CategoryMobileScrollIndicator> y eliminar el styled component si ya no se usa)

// 2. Modificar CategorySelector para mostrar todos los chips de categoría en varias filas, más pequeños
const CategoryMobileScrollIndicator = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0.5rem 0 1rem 0;
    font-size: 0.85rem;
    color: #FDBF37;
    font-style: italic;
    opacity: 0.8;
    gap: 0.5rem;
    user-select: none;
  }
`;

// Agrega un styled para la barra de scroll visual bajo los productos
const CategoryScrollBar = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: block;
    width: 100%;
    max-width: 100%;
    margin: 0.5rem auto 0 auto;
    height: 8px;
    background: #f0f0f0;
    border-radius: 4px;
    position: relative;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(255, 140, 66, 0.08);
  }
`;

const CategoryScrollThumb = styled.div<{ $left: number, $width: number }>`
  position: absolute;
  top: 0;
  left: ${props => props.$left}%;
  height: 100%;
  width: ${props => props.$width}%;
  background: linear-gradient(90deg, var(--primary-orange), #FDBF37);
  border-radius: 4px;
  transition: left 0.2s cubic-bezier(0.4,0,0.2,1);
  box-shadow: 0 2px 8px rgba(255, 140, 66, 0.15);
`;

// 1. Barra de scroll visual para daily deals (solo móvil)
const DailyDealsScrollBar = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: block;
    width: 100%;
    max-width: 100%;
    margin: 0.5rem auto 0 auto;
    height: 8px;
    background: #f0f0f0;
    border-radius: 4px;
    position: relative;
    overflow: hidden;
    /* Quitar box-shadow y borde */
    box-shadow: none;
    border: none;
  }
`;
const DailyDealsScrollThumb = styled.div<{ $left: number, $width: number }>`
  position: absolute;
  top: 0;
  left: ${props => props.$left}%;
  height: 100%;
  width: ${props => props.$width}%;
  background: linear-gradient(90deg, var(--primary-orange), #FDBF37);
  border-radius: 4px;
  transition: left 0.2s cubic-bezier(0.4,0,0.2,1);
  /* Quitar box-shadow y borde */
  box-shadow: none;
  border: none;
`;

// Styled components para la nueva sección de contenedores tipo "Crea espacios únicos"
const UniqueSpacesSection = styled.section`
  width: 100%;
  background: #f3f3f3;
  padding: 2rem 0 1.5rem 0;
`;

const UniqueSpacesTitle = styled.h2`
  text-align: center;
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: #333;
`;

const SpaceSelector = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  padding: 0 20px;

  @media (max-width: 768px) {
    gap: 0.4rem;
    padding: 0 15px;
  }
`;

const SpaceButton = styled.button<{ isActive: boolean }>`
  background: ${props => props.isActive ? 'var(--primary-orange)' : 'white'};
  color: ${props => props.isActive ? 'white' : 'var(--primary-orange)'};
  border: 2px solid var(--primary-orange);
  border-radius: 18px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-weight: 600;
  white-space: nowrap;
  transition: all 0.3s ease;
  flex-shrink: 0;
  font-size: 0.85rem;
  min-width: fit-content;

  &:hover {
    background: var(--primary-orange);
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 140, 66, 0.3);
  }

  @media (max-width: 768px) {
    font-size: 0.75rem;
    padding: 0.4rem 0.8rem;
    border-radius: 14px;
  }
`;

const UniqueSpacesGrid = styled.div`
  display: flex;
  justify-content: center;
  align-items: stretch;
  gap: 1rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  
  @media (max-width: 1100px) {
    flex-direction: column;
    gap: 1rem;
    align-items: center;
    max-width: 600px;
  }
  
  @media (max-width: 768px) {
    padding: 0 15px;
    gap: 0.75rem;
  }
  
  @media (max-width: 480px) {
    padding: 0 10px;
    gap: 0.5rem;
  }
`;

const BigContainer = styled.div`
  flex: 0.4;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  min-height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  
  @media (max-width: 1100px) {
    width: 100%;
    min-height: 250px;
  }
`;

const SmallContainers = styled.div`
  flex: 0.6;
  display: flex;
  flex-direction: row;
  gap: 1rem;
  justify-content: stretch;
  
  @media (max-width: 1100px) {
    width: 100%;
    flex-direction: row;
    gap: 1rem;
  }
  
  @media (max-width: 768px) {
    display: none; /* Ocultar los 3 contenedores en dispositivos móviles */
  }
  
  @media (max-width: 480px) {
    display: none; /* Ocultar los 3 contenedores en dispositivos móviles */
  }
`;

const SmallContainer = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  flex: 1;
  min-height: 320px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 1rem;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 25px rgba(0,0,0,0.15);
    
    /* Mostrar botón flotante al hacer hover */
    div[style*="opacity: 0"] {
      opacity: 1 !important;
    }
    
    /* Efecto zoom en imagen */
    img {
      transform: scale(1.05);
    }
  }

  @media (max-width: 1100px) {
    min-height: 300px;
    padding: 1rem;
    flex: 1;
    min-width: 0;
  }
  
  @media (max-width: 768px) {
    min-height: 280px;
    padding: 0.75rem;
    
    /* Ajustar tamaños de texto en móvil */
    h3 {
      font-size: 0.8rem !important;
    }
    
    p {
      font-size: 0.7rem !important;
    }
    
    span {
      font-size: 0.7rem !important;
    }
    
    button {
      padding: 0.5rem 1rem !important;
      font-size: 0.8rem !important;
    }
  }
  
  @media (max-width: 480px) {
    min-height: 260px;
    padding: 0.5rem;
    
    /* Imagen más pequeña en móviles muy pequeños */
    div[style*="height: 120px"] {
      height: 100px !important;
    }
  }
`;

const ProductTooltipCard = styled.div<{ $visible: boolean; $position?: { top?: string; left?: string; transform?: string; right?: string; bottom?: string; arrowPosition?: string; arrowLeft?: string } }>`
  position: absolute;
  ${props => {
    if (!props.$position) return '';
    const { top, left, transform, right, bottom } = props.$position;
    return `
      ${top ? `top: ${top};` : ''}
      ${left ? `left: ${left};` : ''}
      ${right ? `right: ${right};` : ''}
      ${bottom ? `bottom: ${bottom};` : ''}
      ${transform ? `transform: ${transform};` : ''}
    `;
  }}
  background: white;
  border-radius: 6px;
  box-shadow: 0 3px 15px rgba(0, 0, 0, 0.12);
  padding: 6px;
  width: 180px;
  min-height: 50px;
  z-index: 998;
  opacity: ${props => props.$visible ? 1 : 0};
  visibility: ${props => props.$visible ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  border: 1px solid #e9ecef;
  pointer-events: ${props => props.$visible ? 'auto' : 'none'};
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  cursor: pointer;
  
  &:hover {
    box-shadow: 0 4px 18px rgba(0, 0, 0, 0.18);
  }
`;

const ProductTooltipImage = styled.img`
  width: 32px;
  height: 32px;
  object-fit: cover;
  border-radius: 3px;
  border: 1px solid #e9ecef;
  flex-shrink: 0;
`;

const ProductTooltipContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const ProductTooltipInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const ProductTooltipName = styled.h4`
  margin: 0;
  font-size: 11px;
  font-weight: 600;
  color: #2c3e50;
  line-height: 1.2;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-wrap: break-word;
  max-height: 2.2em;
`;

const ProductTooltipArrow = styled.div`
  width: 24px;
  height: 24px;
  background: linear-gradient(135deg, var(--primary-orange), #FDBF37);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;
  border: 2px solid white;
  box-shadow: 0 2px 6px rgba(255, 140, 66, 0.3);

  &:hover {
    background: linear-gradient(135deg, #FDBF37, var(--primary-orange));
    transform: scale(1.1);
    box-shadow: 0 3px 10px rgba(255, 140, 66, 0.4);
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 10px;
    height: 10px;
    color: white;
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: translateX(1px);
  }
`;



// Global styles for animations
const GlobalStyle = createGlobalStyle`
  @keyframes hotspotGlow {
    0%, 100% {
      box-shadow: 
        0 2px 8px rgba(0,0,0,0.12),
        0 0 0 0 rgba(255, 140, 66, 0);
      transform: scale(1);
    }
    50% {
      box-shadow: 
        0 2px 8px rgba(0,0,0,0.12),
        0 0 0 8px rgba(255, 140, 66, 0.3),
        0 0 0 16px rgba(255, 140, 66, 0.1);
      transform: scale(1);
    }
  }
`;

// Componente Principal
const Home: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { addToConsultation } = useConsultation();
  const [sliderItems, setSliderItems] = useState<SliderItem[]>([]);
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [categoriesWithProducts, setCategoriesWithProducts] = useState<CategoryWithProducts[]>([]);
  const [visitedCategories, setVisitedCategories] = useState<CategoryWithProducts[]>([]);
  const [loading, setLoading] = useState(true);
  const [dailyDeals, setDailyDeals] = useState<DailyDeal[]>([]);
  const [showContent, setShowContent] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [featuredCategories, setFeaturedCategories] = useState<Category[]>([]);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [dailyDealsCurrentSlide, setDailyDealsCurrentSlide] = useState(0);
  const categoryScrollRef = useRef<HTMLDivElement>(null);
  const [categoryScrollThumb, setCategoryScrollThumb] = useState({ left: 0, width: 100 });
  const [showCategoryScrollBar, setShowCategoryScrollBar] = useState(false);
  // Estado para la barra de scroll de daily deals
  const dailyDealsScrollRef = useRef<HTMLDivElement>(null);
  const [dailyDealsScrollThumb, setDailyDealsScrollThumb] = useState({ left: 0, width: 100 });
  const [showDailyDealsScrollBar, setShowDailyDealsScrollBar] = useState(false);
  // Nuevo estado para espacios
  const [spaces, setSpaces] = useState<Space[]>([]);
  const [selectedSpace, setSelectedSpace] = useState<Space | null>(null);
  // Estado para las cantidades de los productos destacados
  const [quantities, setQuantities] = useState<{[key: string]: number}>({});
  const [selectedHotspot, setSelectedHotspot] = useState<{ product: Product; x: number; y: number } | null>(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);

  // Efecto para depurar espacios
  useEffect(() => {
    // Debug logs removed for production
  }, [spaces, selectedSpace]);

  // Efecto para seleccionar el primer espacio cuando se cargan los espacios
  useEffect(() => {
    if (spaces.length > 0 && !selectedSpace) {
      setSelectedSpace(spaces[0]);
    }
  }, [spaces, selectedSpace]);

  // Función para manejar cambios de cantidad
  const handleQuantityChange = (productId: string, delta: number, maxStock: number) => {
    setQuantities(prev => {
      const currentQuantity = prev[productId] || 1;
      const newQuantity = currentQuantity + delta;
      if (newQuantity >= 1) { // Remove stock limit restriction
        return { ...prev, [productId]: newQuantity };
      }
      return prev;
    });
  };

  // Función para calcular posición inteligente del tooltip
  const calculateTooltipPosition = useCallback((x: number, y: number, container: HTMLElement | null) => {
    if (!container) {
      return {
        top: '-80px',
        left: '50%',
        transform: 'translateX(-50%)'
      };
    }

    const containerRect = container.getBoundingClientRect();
    const tooltipWidth = 180;
    const tooltipHeight = 60;
    const margin = 15;
    const hotspotSize = 24;
    
    // Calcular posición del hotspot en píxeles
    const hotspotCenterX = (x / 100) * containerRect.width;
    const hotspotCenterY = (y / 100) * containerRect.height;
    
    let position = {
      top: '',
      left: '',
      right: '',
      bottom: '',
      transform: ''
    };
    
    // Determinar posición vertical - siempre arriba del hotspot
    position.bottom = `${containerRect.height - hotspotCenterY + hotspotSize/2 + 10}px`;
    
    // Determinar posición horizontal
    let tooltipLeft = hotspotCenterX - (tooltipWidth / 2);
    
    if (tooltipLeft < margin) {
      position.left = `${margin}px`;
      position.transform = 'translateX(0)';
    } else if (tooltipLeft + tooltipWidth > containerRect.width - margin) {
      position.right = `${margin}px`;
      position.transform = 'translateX(0)';
    } else {
      position.left = `${hotspotCenterX}px`;
      position.transform = 'translateX(-50%)';
    }
    
    return position;
  }, []);

  // Agregar esta función para manejar el clic en hotspot
  const handleHotspotClick = (product: Product, spaceProduct: SpaceProduct) => {
    if (selectedHotspot && selectedHotspot.product.id === product.id) {
      // Si ya está seleccionado, cerrar el tooltip
      closeTooltip();
    } else {
      // Si no está seleccionado, abrir el tooltip
      setSelectedHotspot({ 
        product, 
        x: spaceProduct.x, 
        y: spaceProduct.y 
      });
      setTooltipVisible(true);
    }
  };

  // Función para cerrar el tooltip
  const closeTooltip = () => {
    setTooltipVisible(false);
    setTimeout(() => setSelectedHotspot(null), 300);
  };

  // Función para navegar al producto
  const handleViewProduct = () => {
    if (selectedHotspot) {
      navigate(`/producto/${selectedHotspot.product.id}`);
      closeTooltip();
    }
  };

  // Función para agregar todos los productos de un espacio
  const handleAddAllProducts = useCallback(async (space: Space) => {
    try {
      // Obtener todos los productos únicos del espacio (destacados y no destacados)
      const allProductIds = Array.from(new Set([
        ...space.products.map(p => p.product_id),
        ...space.destacados.map(p => p.product_id)
      ]));
      
      if (allProductIds.length === 0) {
        toast.warning('No hay productos disponibles en este espacio');
        return;
      }
      
      // Obtener detalles de todos los productos
      const productPromises = allProductIds.map(id => 
        productService.getProduct(id.toString())
      );
      
      const products = await Promise.all(productPromises);
      
      // Agregar cada producto a la consulta
      let addedCount = 0;
      products.forEach(product => {
        if (product) { // Remove stock check
          addToConsultation(product, 1);
          addedCount++;
        }
      });
      
      if (addedCount > 0) {
        toast.success(`${addedCount} producto(s) agregado(s) a la consulta`);
      } else {
        toast.warning('No se pudieron agregar productos');
      }
      
    } catch (error) {
      console.error('Error adding products:', error);
      toast.error('Error al agregar productos a la consulta');
    }
  }, [isAuthenticated, navigate, addToConsultation]);

  const handleViewCategory = (categoryId: string) => {
    const category = categoriesWithProducts.find(c => c.category.id === categoryId);
    if (category) {
      navigate(`/productos?categoria=${category.category.name}`);
    }
  };

  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategory(categoryId);
  };

  const getFeaturedProductsForCategory = (categoryId: string): Product[] => {
    const category = featuredCategories.find(c => c.id === categoryId);
    
    // Type guard más robusto
    if (!category || 
        !category.home_featured_products || 
        !Array.isArray(category.home_featured_products) || 
        category.home_featured_products.length === 0) {
      return [];
    }
    
    // Asegurar que home_featured_products es tratado como string[]
    const featuredProductIds = category.home_featured_products as string[];
    
    const categoryProducts = allProducts
      .filter((product: Product) => {
        const productIdStr = String(product.id);
        return featuredProductIds.some(featuredId => String(featuredId) === productIdStr);
      })
      .slice(0, 5); // Limitar a 5 productos para la vista de categoría
      
    return categoryProducts;
  };

  // Funciones para el carrusel de daily deals
  // INICIALIZAR isMobile correctamente desde el principio
  const [isMobile, setIsMobile] = useState(() => {
    // Solo ejecutar en el cliente, no en SSR
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 768;
    }
    return false;
  });
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [scrollProgress, setScrollProgress] = useState(0); // Para la barra de progreso
  const carouselRef = useRef<HTMLDivElement>(null); // Ref para el container

  // Lógica diferente para desktop vs móvil
  const validDeals = useMemo(() => {
    return dailyDeals.filter(deal => 
      deal.product && 
      deal.active &&
      deal.product.name
    );
  }, [dailyDeals]);

  // Función para manejar el scroll horizontal EN PANTALLAS MÓVILES
  const updateScrollProgress = useCallback(() => {
    const container = carouselRef.current;
    // Verificar si la pantalla es pequeña (CSS maneja el scroll automáticamente)
    const isSmallScreen = typeof window !== 'undefined' && window.innerWidth <= 768;
    if (!container || !isSmallScreen) return;

    try {
      const scrollLeft = container.scrollLeft;
      const maxScroll = container.scrollWidth - container.clientWidth;
      

      
      if (maxScroll > 0) {
        const progress = (scrollLeft / maxScroll) * 75; // Máximo 75% del ancho de la barra
        const newProgress = Math.max(0, Math.min(progress, 75));
        setScrollProgress(newProgress);
        

      } else {
        setScrollProgress(0);
      }
    } catch (error) {
      // Error updating scroll progress
    }
  }, []);

  // UN SOLO useEffect para TODO: detección móvil, scroll y inicialización  
  useEffect(() => {
    // 1. DETECTAR MÓVIL primero (verificación inicial inmediata)
    const checkMobile = () => {
      const isMobileNow = window.innerWidth <= 768;

      if (isMobileNow !== isMobile) {
        setIsMobile(isMobileNow);
      }
    };
    
    // Verificar inmediatamente al montar
    checkMobile();
    
    // 2. CONFIGURAR RESIZE LISTENER
    const handleResize = () => {
      checkMobile();
    };
    
    window.addEventListener('resize', handleResize);
    
    // 3. CONFIGURAR SCROLL solo si es pantalla pequeña y hay datos
    const container = carouselRef.current;
    const isSmallScreen = typeof window !== 'undefined' && window.innerWidth <= 768;
    let scrollCleanup: (() => void) | null = null;
    
    if (container && isSmallScreen && validDeals.length > 0) {


      // Función de scroll con throttling
      let ticking = false;
      const handleScroll = () => {
        if (!ticking) {
          requestAnimationFrame(() => {
            updateScrollProgress();
            ticking = false;
          });
          ticking = true;
        }
      };

      // Configurar el event listener
      container.addEventListener('scroll', handleScroll, { passive: true });
      
      // Inicialización múltiple para asegurar que funcione
      const initializeProgress = () => updateScrollProgress();
      
      // Inmediato
      initializeProgress();
      
      // Después de pequeños delays
      const timer1 = setTimeout(initializeProgress, 50);
      const timer2 = setTimeout(initializeProgress, 200);
      const timer3 = setTimeout(initializeProgress, 500);

      // También en load
      const handleLoad = () => setTimeout(initializeProgress, 100);
      window.addEventListener('load', handleLoad);

      // Función de cleanup para el scroll
      scrollCleanup = () => {
        if (container) {
          container.removeEventListener('scroll', handleScroll);
        }
        window.removeEventListener('load', handleLoad);
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
      };
    } else {
      // Si no es móvil o no hay datos, resetear progreso
      setScrollProgress(0);
    }

    // 4. CLEANUP
    return () => {
      window.removeEventListener('resize', handleResize);
      if (scrollCleanup) {
        scrollCleanup();
      }
    };
  }, [isMobile, validDeals.length, updateScrollProgress]);

  // useEffect ADICIONAL para configurar scroll cuando isMobile cambie (DESPUÉS del render)
  useEffect(() => {
    const isSmallScreen = typeof window !== 'undefined' && window.innerWidth <= 768;
    if (!isSmallScreen || validDeals.length === 0) {
      setScrollProgress(0);
      return;
    }

    // Delay pequeño para asegurar que el DOM esté completamente renderizado
    const configureScrollDelay = setTimeout(() => {
      const container = carouselRef.current;
      if (container) {
        updateScrollProgress();
        
        // Re-inicializar después de un momento más
        setTimeout(() => {
          updateScrollProgress();
        }, 100);
      }
    }, 50);

    return () => {
      clearTimeout(configureScrollDelay);
    };
  }, [isMobile, updateScrollProgress]);

  // Desktop: slides completos, Móvil: scroll individual
  const maxVisibleItems = isMobile ? 2.5 : 5; // 2.5 productos visibles en móvil
  const maxSlideIndex = isMobile 
    ? Math.max(0, validDeals.length - Math.floor(maxVisibleItems)) // Móvil: producto por producto
    : Math.max(0, Math.ceil(validDeals.length / 5) - 1); // Desktop: slides completos

  // Reset slide cuando cambien los deals
  useEffect(() => {
    if (dailyDealsCurrentSlide > maxSlideIndex) {
      setDailyDealsCurrentSlide(Math.max(0, maxSlideIndex));
    }
  }, [maxSlideIndex, dailyDealsCurrentSlide]);

  const nextDailyDealsSlide = () => {
    if (dailyDealsCurrentSlide < maxSlideIndex) {
      setDailyDealsCurrentSlide(prev => prev + 1);
    }
  };

  const prevDailyDealsSlide = () => {
    if (dailyDealsCurrentSlide > 0) {
      setDailyDealsCurrentSlide(prev => prev - 1);
    }
  };

  // Funciones para swipe en móvil
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      nextDailyDealsSlide();
    }
    if (isRightSwipe) {
      prevDailyDealsSlide();
    }
  };

  // Eliminar función goToSlide ya que no se usa más
  // const goToSlide = (slideIndex: number) => {
  //   setDailyDealsCurrentSlide(slideIndex);
  // };

  // Eliminar la función getDealsSlides ya que ahora usamos useMemo
  // const getDealsSlides = () => {
  //   const slides = [];
  //   for (let i = 0; i < dailyDeals.length; i += itemsPerSlide) {
  //     slides.push(dailyDeals.slice(i, i + itemsPerSlide));
  //   }
  //   return slides;
  // };
  
  // const dealsSlides = getDealsSlides();

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Cargar datos existentes
        const [sliderData, categoriesData, dealsData, allProductsData] = await Promise.all([
          sliderService.getSliderItems(),
          categoryService.getCategories(),
          dailyDealService.getDailyDeals(),
          productService.getAllProducts()
        ]);

        setSliderItems(sliderData);
        setAllProducts(allProductsData);
        setDailyDeals(dealsData);

        // Filtrar productos destacados localmente
        const featuredData = allProductsData.filter(p => p.featured);
        setFeaturedProducts(featuredData);

        // Procesar categorías con productos
        const categoriesWithProductsData = await Promise.all(
          categoriesData.map(async (category) => {
            try {
              const products = await productService.getProductsByCategory(category.id);
              return {
                category: {
                  ...category,
                  image_url: category.image_url || '/images/default-category.png'
                },
                products: products.products || []
              };
            } catch (error) {
              console.error(`Error cargando productos para categoría ${category.id}:`, error);
              return {
                category: {
                  ...category,
                  image_url: category.image_url || '/images/default-category.png'
                },
                products: []
              };
            }
          })
        );

        setCategoriesWithProducts(categoriesWithProductsData);

        // Obtener categorías visitadas usando el método correcto (solo las 3 más recientes)
        const visitedCategoriesData = categoryHistory.get()
          .slice(0, 3)
          .map(visitedCat => {
            const category = categoriesData.find(c => String(c.id) === String(visitedCat.id));
            if (!category) return null;
            
            return {
              category: {
                ...category,
                image_url: visitedCat.image_url
              },
              products: allProductsData
                .filter(product => product.category_id === category.id)
                .slice(0, 3)
            };
          })
          .filter((item): item is CategoryWithProducts => item !== null);
        
        setVisitedCategories(visitedCategoriesData);

        // Filtrar categorías destacadas
        const featuredCategoriesData = categoriesData.filter(category => 
          category.home_featured_products && 
          Array.isArray(category.home_featured_products) && 
          category.home_featured_products.length > 0
        );
        setFeaturedCategories(featuredCategoriesData);

        // Seleccionar primera categoría destacada si existe
        if (featuredCategoriesData.length > 0) {
          setSelectedCategory(featuredCategoriesData[0].id);
        }

        // Cargar espacios
        await loadSpaces();

      } catch (error) {
        console.error('Error cargando datos:', error);
        toast.error('Error al cargar los datos');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handlePreloaderComplete = () => {
    setShowContent(true);
  };

  // Actualiza la barra de scroll al hacer scroll horizontal
  useEffect(() => {
    if (typeof window === 'undefined' || window.innerWidth > 768) {
      setShowCategoryScrollBar(false);
      return;
    }
    const container = categoryScrollRef.current;
    if (!container) return;
    const handleScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      if (scrollWidth <= clientWidth) {
        setCategoryScrollThumb({ left: 0, width: 100 });
        setShowCategoryScrollBar(false);
        return;
      }
      setShowCategoryScrollBar(true);
      const thumbWidth = (clientWidth / scrollWidth) * 100;
      const maxScroll = scrollWidth - clientWidth;
      const left = (scrollLeft / maxScroll) * (100 - thumbWidth);
      setCategoryScrollThumb({ left, width: thumbWidth });
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    // Forzar el cálculo después de renderizar los productos (doble timeout)
    const timeout1 = setTimeout(() => {
      handleScroll();
      const timeout2 = setTimeout(handleScroll, 50);
      return () => clearTimeout(timeout2);
    }, 50);
    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(timeout1);
    };
  }, [selectedCategory, getFeaturedProductsForCategory(selectedCategory || '').length]);

  // Barra de scroll visual para daily deals (solo móvil y solo si hay overflow)
  useEffect(() => {
    if (typeof window === 'undefined' || window.innerWidth > 768) {
      setShowDailyDealsScrollBar(false);
      return;
    }
    const container = dailyDealsScrollRef.current;
    if (!container) return;
    const handleScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      if (scrollWidth <= clientWidth) {
        setDailyDealsScrollThumb({ left: 0, width: 100 });
        setShowDailyDealsScrollBar(false);
        return;
      }
      setShowDailyDealsScrollBar(true);
      const thumbWidth = (clientWidth / scrollWidth) * 100;
      const maxScroll = scrollWidth - clientWidth;
      const left = (scrollLeft / maxScroll) * (100 - thumbWidth);
      setDailyDealsScrollThumb({ left, width: thumbWidth });
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    // Forzar el cálculo después de renderizar los productos (doble timeout)
    const timeout1 = setTimeout(() => {
      handleScroll();
      const timeout2 = setTimeout(handleScroll, 50);
      return () => clearTimeout(timeout2);
    }, 50);
    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(timeout1);
    };
  }, [isMobile, validDeals.length]);

  // Cargar espacios desde el backend
  const loadSpaces = async () => {
    try {
      const response = await fetch(`${API_URL}/spaces`);
      if (response.ok) {
        const spacesData: Space[] = await response.json();
        setSpaces(spacesData);
        // El useEffect se encargará de seleccionar el primer espacio
      } else {
        // Error en la respuesta del servidor
      }
    } catch (error) {
      // Error cargando espacios
    }
  };

  // Obtener productos destacados del espacio seleccionado
  const getSpaceDestacados = (): Product[] => {
    
    if (!selectedSpace) {
      return [];
    }
    
    if (!selectedSpace.destacados || selectedSpace.destacados.length === 0) {
      return [];
    }
    
    const destacados = selectedSpace.destacados
      .map((spaceProduct) => {
        // Buscar el producto en allProducts
        const product = allProducts.find(p => {
          const productId = Number(p.id);
          const spaceProductId = Number(spaceProduct.product_id);
          return productId === spaceProductId;
        });
        
        return product;
      })
      .filter(Boolean) as Product[];
    return destacados;
  };

  return (
    <>
      <GlobalStyle />
      <Preloader loading={loading} onComplete={handlePreloaderComplete} />
      
      {showContent && (
        <HomeContainer>
          <HeroSection>
            <Slider slides={sliderItems} autoPlay={true} autoPlayInterval={5000} />
          </HeroSection>

          {visitedCategories.length > 0 && (
            <Section style={{ background: 'white' }}>
              <SectionTitle>
                <StyledHistoryIcon />
                Últimas Categorías que Visitaste
              </SectionTitle>
              <VisitedCategoriesGrid>
                {visitedCategories.map(({ category, products }) => (
                  <VisitedCategoryCard
                    key={category.id}
                    onClick={() => handleViewCategory(category.id)}
                  >
                    <CategoryOverlay>
                      <CategoryTop>
                        <CategoryInfo>
                          <CategoryName>{category.name}</CategoryName>
                          <CategorySubtitle>Explora los productos que más te interesan</CategorySubtitle>
                        </CategoryInfo>
                        <CategoryImage
                          src={getImageUrl(category.image_url) || '/images/default-category.png'}
                          alt={category.name}
                          onError={(e) => {
                            if (e && e.target) {
                              const target = e.target as HTMLImageElement;
                              target.src = '/images/default-category.png';
                            }
                          }}
                        />
                      </CategoryTop>
                      
                      <ProductsPreview>
                        {products.slice(0, 3).map((product, index) => (
                          <img
                            key={`${category.id}-${product.id}-${index}`}
                            src={getImageUrl(product.image_url) || '/placeholder-image.jpg'}
                            alt={product.name}
                            onError={(e) => {
                              if (e && e.target) {
                                const target = e.target as HTMLImageElement;
                                target.src = '/placeholder-image.jpg';
                              }
                            }}
                          />
                        ))}
                      </ProductsPreview>
                    </CategoryOverlay>
                  </VisitedCategoryCard>
                ))}
              </VisitedCategoriesGrid>
            </Section>
          )}

          <SmallSliderSection>
            <SmallSliderContainer>
              <SectionTitle>
                <StyledFireIcon />
                ¡Llévatelos hoy!
              </SectionTitle>
              
              {dailyDeals.length > 0 && (
                <>
                  {/* Container - CSS maneja el scroll automáticamente */}
                  <DailyDealsCarouselContainer 
                    ref={dailyDealsScrollRef}
                  >
                    <DailyDealsCarouselTrack 
                      currentSlide={dailyDealsCurrentSlide}
                      isMobile={isMobile}
                      totalProducts={validDeals.length}
                    >
                      {isMobile ? (
                        // Móvil: Una sola fila con todos los productos para scroll nativo
                        <DailyDealsCarouselSlide 
                          totalProducts={validDeals.length}
                          isMobile={isMobile}
                        >
                          {validDeals.map((deal: DailyDeal, index: number) => (
                            <DailyDealsItemWrapper key={`${deal.id}-${index}`}>
                              <DailyDealCard deal={deal} />
                            </DailyDealsItemWrapper>
                          ))}
                        </DailyDealsCarouselSlide>
                      ) : (
                        // Desktop: Slides completos como SIEMPRE estuvo (5 productos por slide)
                        (() => {
                          const dealsSlides: DailyDeal[][] = [];
                          for (let i = 0; i < validDeals.length; i += 5) {
                            dealsSlides.push(validDeals.slice(i, i + 5));
                          }
                          return dealsSlides.map((slideDeals: DailyDeal[], slideIndex: number) => (
                            <DailyDealsCarouselSlide key={slideIndex}>
                              {slideDeals.map((deal: DailyDeal, index: number) => (
                                <DailyDealsItemWrapper key={`${deal.id}-${index}`}>
                                  <DailyDealCard deal={deal} />
                                </DailyDealsItemWrapper>
                              ))}
                            </DailyDealsCarouselSlide>
                          ));
                        })()
                      )}
                  

                    </DailyDealsCarouselTrack>
                    
                    {/* Botones SOLO para desktop */}
                    {!isMobile && maxSlideIndex > 0 && (
                      <>
                        <DailyDealsNavButton 
                          direction="left" 
                          onClick={prevDailyDealsSlide}
                          disabled={dailyDealsCurrentSlide === 0}
                        >
                          <StyledChevronLeftIcon />
                        </DailyDealsNavButton>
                        <DailyDealsNavButton 
                          direction="right" 
                          onClick={nextDailyDealsSlide}
                          disabled={dailyDealsCurrentSlide >= maxSlideIndex}
                        >
                          <StyledChevronRightIcon />
                        </DailyDealsNavButton>
                      </>
                    )}
                  </DailyDealsCarouselContainer>
                  
                  {/* Barra de progreso para pantallas móviles */}
                  {showDailyDealsScrollBar && (
                    <DailyDealsScrollBar>
                      <DailyDealsScrollThumb $left={dailyDealsScrollThumb.left} $width={dailyDealsScrollThumb.width} />
                    </DailyDealsScrollBar>
                  )}
                </>
              )}
            </SmallSliderContainer>
          </SmallSliderSection>

          <Section>
            <SectionTitle>
              
              Productos Destacados
            </SectionTitle>
            {featuredProducts.length > 0 ? (
              <>
                <ProductGrid>
                  {featuredProducts.slice(0, 4).map(product => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </ProductGrid>
                <ViewAllButton onClick={() => navigate('/productos?filter=featured')}>
                  Ver Todos los Destacados
                </ViewAllButton>
              </>
            ) : (
              <EmptyState>
                No hay productos destacados disponibles en este momento.
              </EmptyState>
            )}
          </Section>

          {featuredCategories.length > 0 && (
            <Section>
              <SectionTitle>
                <StyledGiftIcon />
                Explora por Categorías
              </SectionTitle>
              
              <CategorySection>
                <CategorySelector>
                  {featuredCategories.map(category => (
                    <CategoryButton
                      key={category.id}
                      isActive={selectedCategory === category.id}
                      onClick={() => handleCategorySelect(category.id)}
                    >
                      {category.name}
                    </CategoryButton>
                  ))}
                </CategorySelector>
              </CategorySection>
              
              {selectedCategory && (
                <CategoryProductsSection>
                  <CategoryProductsContainer ref={categoryScrollRef}>
                    <CategoryProductsGrid>
                      {getFeaturedProductsForCategory(selectedCategory).map(product => (
                        <ProductCard key={product.id} product={product} hideStockInfo />
                      ))}
                    </CategoryProductsGrid>
                  </CategoryProductsContainer>
                  {/* Barra de scroll visual solo en móvil */}
                  <CategoryScrollBar>
                    <CategoryScrollThumb $left={categoryScrollThumb.left} $width={categoryScrollThumb.width} />
                  </CategoryScrollBar>
                  {getFeaturedProductsForCategory(selectedCategory).length > 0 && (
                    <DynamicCategoryButton 
                      onClick={() => {
                        const category = featuredCategories.find(c => c.id === selectedCategory);
                        if (category) {
                          navigate(`/productos?categoria=${category.name}`);
                        }
                      }}
                    >
                      Ver todo {featuredCategories.find(c => c.id === selectedCategory)?.name}
                      <StyledArrowRightIcon />
                    </DynamicCategoryButton>
                  )}
                </CategoryProductsSection>
              )}
            </Section>
          )}

          {/* NUEVA SECCIÓN: Crea espacios únicos */}
          {spaces.length > 0 && selectedSpace && (
            <UniqueSpacesSection>
              <UniqueSpacesTitle>Crea espacios únicos</UniqueSpacesTitle>
              
              {/* SELECTOR DE ESPACIOS */}
              {spaces.length > 1 && (
                <SpaceSelector>
                  {spaces.map((space) => (
                    <SpaceButton
                      key={space.id}
                      isActive={selectedSpace?.id === space.id}
                      onClick={() => setSelectedSpace(space)}
                    >
                      {space.name || `Espacio ${space.id}`}
                    </SpaceButton>
                  ))}
                </SpaceSelector>
              )}
              
              <UniqueSpacesGrid>
                <BigContainer>
                  {selectedSpace.image_url && (
                    <div className="space-container space-container" style={{ position: 'relative', width: '100%', height: '100%' }}>
                      <img 
                        src={getImageUrl(selectedSpace.image_url)} 
                        alt="Espacio único" 
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'cover' 
                        }} 
                      />
                      
                      {/* Botón Agregar Todo - REPOSICIONADO */}
                      <div style={{
                        position: 'absolute',
                        bottom: '1rem',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 20
                      }}>
                        <button
                          onClick={() => handleAddAllProducts(selectedSpace)}
                          disabled={!selectedSpace?.products?.length}
                          style={{
                            background: selectedSpace?.products?.length 
                              ? 'linear-gradient(135deg, #FDBF37, #FDBF37)' 
                              : '#bdc3c7',
                            color: 'white',
                            border: 'none',
                            padding: '0.5rem 1rem',
                            borderRadius: '20px',
                            fontWeight: '600',
                            fontSize: '0.9rem',
                            cursor: selectedSpace?.products?.length ? 'pointer' : 'not-allowed',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            transition: 'all 0.3s ease',
                            boxShadow: '0 2px 8px rgba(244, 154, 6, 0.3)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}
                          onMouseEnter={(e) => {
                            if (selectedSpace?.products?.length) {
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(244, 154, 6, 0.4)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(244, 154, 6, 0.3)';
                          }}
                        >
                          <IconComponent icon={FaShoppingCart} color="currentColor" />
                          {!selectedSpace?.products?.length 
                            ? 'Sin productos'
                            : 'AGREGAR TODO'
                          }
                        </button>
                      </div>
                      {/* Hotspots */}
                      {selectedSpace.products && selectedSpace.products.map((spaceProduct, index) => {
                        const product = allProducts.find(p => Number(p.id) === spaceProduct.product_id);
                        if (!product) {
                          return null;
                        }
                        return (
                          <div
                            key={index}
                            style={{
                              position: 'absolute',
                              left: `calc(${spaceProduct.x}% - 12px)`,
                              top: `calc(${spaceProduct.y}% - 12px)`,
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              background: spaceProduct.destacado ? '#FDBF37' : '#aaa',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                              border: '2px solid #fff',
                              cursor: 'pointer',
                              zIndex: 999, // Menor que el header (1002)
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.3s ease',
                              animation: spaceProduct.destacado ? 'hotspotGlow 2s ease-in-out infinite' : 'none'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleHotspotClick(product, spaceProduct);
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'scale(1.2)';
                              e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.2)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'scale(1)';
                              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.12)';
                            }}
                            title={product.name}
                          >
                            {selectedHotspot && selectedHotspot.product.id === product.id ? (
                              <Times 
                                width={12} 
                                height={12} 
                                style={{ 
                                  color: '#fff',
                                  filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.8))',
                                  zIndex: 1003,
                                  cursor: 'pointer'
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  closeTooltip();
                                }}
                              />
                            ) : (
                              <div style={{ 
                                width: 10, 
                                height: 10, 
                                borderRadius: '50%', 
                                background: spaceProduct.destacado ? '#FDBF37' : '#aaa', 
                                boxShadow: '0 0 0 4px #fff' 
                              }} />
                            )}
                          </div>
                        );
                      })}
                      
                      {/* Tooltip renderizado fuera de los hotspots */}
                      {selectedHotspot && tooltipVisible && (() => {
                        const product = allProducts.find(p => p.id === selectedHotspot.product.id);
                        if (!product) return null;
                        
                        return (
                          <ProductTooltipCard 
                            $visible={tooltipVisible} 
                            $position={calculateTooltipPosition(
                              selectedHotspot.x, 
                              selectedHotspot.y, 
                              document.querySelector('.space-container') as HTMLElement
                            )}
                            onClick={(e) => {
                              console.log('Tooltip card clicked, navigating to product:', product.id);
                              if (product?.id) {
                                navigate(`/producto/${product.id}`);
                                closeTooltip();
                              }
                            }}
                            style={{
                              position: 'absolute',
                              zIndex: 1001
                            }}
                          >
                            <ProductTooltipContent>
                              <ProductTooltipImage 
                                src={getImageUrl(product.image_url)} 
                                alt={product.name}
                                onError={(e) => {
                                  e.currentTarget.src = '/placeholder-image.jpg';
                                }}
                              />
                              <ProductTooltipInfo>
                                <ProductTooltipName>{product.name}</ProductTooltipName>
                              </ProductTooltipInfo>
                            </ProductTooltipContent>
                            <ProductTooltipArrow>
                              <ArrowRight />
                            </ProductTooltipArrow>
                          </ProductTooltipCard>
                        );
                      })()}
                      
                      {/* Overlay para cerrar tooltip al hacer clic fuera */}
                      {tooltipVisible && (
                        <div
                          style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 999, // Menor que hotspots y tooltip
                            background: 'transparent',
                            pointerEvents: 'auto'
                          }}
                          onClick={closeTooltip}
                        />
                      )}
                    </div>
                  )}
                </BigContainer>
                <SmallContainers>
                  {getSpaceDestacados().slice(0, 3).map((product, index) => (
                    <SmallContainer key={product.id || index}>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        maxWidth: '200px',
                        position: 'relative'
                      }}>
                        {/* Imagen del producto */}
                        <div style={{
                          position: 'relative',
                          width: '100%',
                          height: '120px',
                          marginBottom: '1rem',
                          borderRadius: '8px',
                          overflow: 'hidden'
                        }}>
                          <img 
                            src={getImageUrl(product.image_url)} 
                            alt={product.name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                              transition: 'transform 0.3s ease'
                            }}
                          />
                          
                          {/* Botón de acción flotante */}
                          <div style={{
                            position: 'absolute',
                            top: '10px',
                            right: '10px',
                            opacity: 0,
                            transition: 'opacity 0.3s ease'
                          }}>
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/producto/${product.id}`);
                              }}
                              style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                border: 'none',
                                background: 'rgba(255,255,255,0.9)',
                                color: '#2c3e50',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                backdropFilter: 'blur(10px)'
                              }}
                              title="Ver detalles"
                            >
                              <Eye width={16} height={16} style={{ color: '#2c3e50' }} />
                            </button>
                          </div>
                        </div>
                        
                        {/* Contenido igual que ProductCard */}
                        <div style={{
                          padding: '0 0.75rem',
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column'
                        }}>
                          {/* Categoría */}
                          <span style={{
                            color: '#3498db',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            marginBottom: '0.5rem'
                          }}>
                            CONSTRUCCIÓN
                          </span>
                          
                          {/* Nombre del producto */}
                          <h3 style={{ 
                            color: '#2c3e50',
                            fontSize: '0.85rem',
                            marginBottom: '0.5rem',
                            fontWeight: '600',
                            lineHeight: '1.2',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}>
                            {product.name}
                          </h3>
                          
                          {/* Descripción */}
                          <p style={{ 
                            color: '#7f8c8d',
                            fontSize: '0.75rem',
                            lineHeight: '1.4',
                            marginBottom: '1rem',
                            flex: 1,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}>
                            {product.description || 'Producto importado desde Excel'}
                          </p>
                          
                          {/* Especificaciones si existen */}
                          {(product.brand || product.model) && (
                            <div style={{ marginBottom: '1rem' }}>
                              {product.brand && (
                                <span style={{
                                  display: 'inline-block',
                                  background: '#ecf0f1',
                                  color: '#2c3e50',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '12px',
                                  fontSize: '0.8rem',
                                  marginRight: '0.5rem',
                                  marginBottom: '0.25rem'
                                }}>
                                  Marca: {product.brand}
                                </span>
                              )}
                              {product.model && (
                                <span style={{
                                  display: 'inline-block',
                                  background: '#ecf0f1',
                                  color: '#2c3e50',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '12px',
                                  fontSize: '0.7rem',
                                  marginRight: '0.5rem',
                                  marginBottom: '0.25rem'
                                }}>
                                  Modelo: {product.model}
                                </span>
                              )}
                            </div>
                          )}
                          
                          {/* Controles de cantidad */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            marginBottom: '1rem'
                          }} onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => handleQuantityChange(String(product.id), -1, 999999)} // Remove stock limit
                              style={{
                                background: '#f0f0f0',
                                border: 'none',
                                borderRadius: '50%',
                                width: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                fontSize: '0.8rem'
                              }}
                            >
                              <IconComponent icon={FaMinus} color="currentColor" />
                            </button>
                            <span style={{
                              fontSize: '1rem',
                              minWidth: '24px',
                              textAlign: 'center'
                            }}>
                              {quantities[String(product.id)] || 1}
                            </span>
                            <button
                              onClick={() => handleQuantityChange(String(product.id), 1, 999999)} // Remove stock limit
                              style={{
                                background: '#f0f0f0',
                                border: 'none',
                                borderRadius: '50%',
                                width: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                fontSize: '0.8rem'
                              }}
                            >
                              <IconComponent icon={FaPlus} color="currentColor" />
                            </button>
                          </div>
                          
                          {/* Footer con botón consultar */}
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginTop: 'auto'
                          }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const quantity = quantities[String(product.id)] || 1;
                                addToConsultation(product, quantity);
                                toast.success(`${quantity} ${product.name} agregado(s) a la consulta`);
                              }}
                              // disabled={product.stock === 0} // Remove stock check
                              style={{
                                background: '#FDBF37', // Always enabled
                                color: 'white',
                                border: 'none',
                                padding: '0.75rem 1.5rem',
                                borderRadius: '25px',
                                fontWeight: '600',
                                cursor: 'pointer', // Always clickable
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                transition: 'all 0.3s ease',
                                fontSize: '0.7rem',
                                marginLeft: 'auto'
                              }}
                            >
                              <IconComponent icon={FaShoppingCart} color="currentColor" />
                              Agregar al carrito
                            </button>
                          </div>
                        </div>
                      </div>
                    </SmallContainer>
                  ))}
                  {/* Rellenar contenedores vacíos si hay menos de 3 productos destacados */}
                  {Array.from({ length: Math.max(0, 3 - getSpaceDestacados().length) }).map((_, index) => (
                    <SmallContainer key={`empty-${index}`}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        height: '100%',
                        color: '#999',
                        fontSize: '0.9rem',
                        textAlign: 'center'
                      }}>
                        Producto destacado
                      </div>
                    </SmallContainer>
                  ))}
                </SmallContainers>
              </UniqueSpacesGrid>
              
             
            </UniqueSpacesSection>
          )}

          {/* Carrusel de Marcas */}
          <BrandsCarousel />
        </HomeContainer>
      )}
      
      {/* Speed Dial Flotante */}
      <FloatingSpeedDial />
    </>
  );
};

export default Home;